<template>
<section class="hero">
  <div class="hero__bg" aria-hidden="true"></div>
  <div class="hero__inner" :class="{ 'hero__inner--no-visual': hideVisual }">
    <div class="hero__copy">
      <h1 class="hero__title">{{ title }}</h1>

      <p v-if="normalizedDescriptionParts.length" class="hero__desc">
        <template v-for="(part, partIndex) in normalizedDescriptionParts">
          <strong
            v-if="part.type === 'strong'"
            :key="`hero-desc-strong-${partIndex}`"
          >{{ part.value }}</strong>
          <span v-else :key="`hero-desc-text-${partIndex}`">{{ part.value }}</span>
        </template>
      </p>
      <p v-else class="hero__desc">{{ description }}</p>

      <ul v-if="heroPoints.length" class="hero__points">
        <li v-for="item in heroPoints" :key="item.text">
          <span class="hero__point-icon" v-html="item.icon"></span>
          <span>{{ item.text }}</span>
        </li>
      </ul>

      <p
        v-if="note && noteBeforeCta"
        class="hero__note hero__note--before"
      ><strong>{{ note }}</strong></p>

      <div class="hero__actions">
        <a class="hero__btn hero__btn--primary" :href="ctaTo" @click.prevent="goCta">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
            <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 19h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ ctaLabel }}
        </a>
        <a
          v-if="showPlayStore"
          class="hero__btn hero__btn--play"
          :href="playStoreUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path fill="currentColor" d="M3.6 2.8c-.4.2-.6.6-.6 1.1v16.2c0 .5.2.9.6 1.1l10.6-9.2L3.6 2.8zm12.1 8.2l2.5-1.4-2.5-1.4-1.5 1.4 1.5 1.4zm-1.8 1.6l-1.8 1.6 6.5 3.7c.5-.3.8-.8.8-1.4v-.2l-5.5-3.7zm0-4.8l5.5-3.7v-.2c0-.6-.3-1.1-.8-1.4l-6.5 3.7 1.8 1.6zM14.2 12L3.6 21.2c.1 0 .2.1.3.1.2 0 .4-.1.6-.2L15.8 14l-1.6-2z" />
          </svg>
          Google Play
        </a>
      </div>
      <p v-if="note && !noteBeforeCta" class="hero__note">{{ note }}</p>
    </div>

    <div v-if="!hideVisual" class="hero__visual">
      <img
        v-if="image"
        class="hero__platform-image"
        :src="image"
        :alt="imageAlt"
        loading="eager"
      >
      <template v-else>
        <img
          class="hero__privacy"
          src="@/assets/vpn/privacy.webp"
          alt="Secure VPN protects your privacy"
          width="520"
          height="520"
          loading="eager"
        >
        <img
          class="hero__phone"
          src="@/assets/vpn/hero-phone.png"
          alt="Secure VPN app on Android"
          width="480"
          height="854"
          loading="eager"
        >
      </template>
    </div>
  </div>

  <!-- Available on：单独一行平铺在 Hero 主区下方 -->
  <div class="hero__platforms">
    <div class="hero__platforms-inner">
      <p class="hero__platforms-title">Available on</p>
      <ul class="hero__platforms-list">
        <li v-for="item in platforms" :key="item.label">
          <a
            v-if="item.external"
            class="hero__platforms-link"
            :href="item.to"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span class="hero__platforms-icon" v-html="item.icon" aria-hidden="true"></span>
            <span class="hero__platforms-label">{{ item.label }}</span>
          </a>
          <router-link v-else-if="item.to" class="hero__platforms-link" :to="item.to">
            <span class="hero__platforms-icon" v-html="item.icon" aria-hidden="true"></span>
            <span class="hero__platforms-label">{{ item.label }}</span>
          </router-link>
          <span v-else class="hero__platforms-link hero__platforms-link--static">
            <span class="hero__platforms-icon" v-html="item.icon" aria-hidden="true"></span>
            <span class="hero__platforms-label">{{ item.label }}</span>
          </span>
        </li>
      </ul>
    </div>
  </div>
</section>
</template>

<script>
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.secure.vpn.private.browser&referrer=utm_source%3Dweb';

export default {
  name: 'HeroSection',
  props: {
    title: {
      type: String,
      default: 'Free Secure VPN App for Private Browsing',
    },
    description: {
      type: String,
      default:
        'Browse the internet safely with a fast, encrypted VPN connection. Secure VPN helps protect your online privacy, access global servers, and enjoy unlimited bandwidth, all for free.',
    },
    descriptionParts: {
      type: Array,
      default: null,
    },
    points: {
      type: Array,
      default: null,
    },
    ctaLabel: {
      type: String,
      default: 'Get Secure VPN',
    },
    ctaTo: {
      type: String,
      default: '/download/',
    },
    image: {
      type: String,
      default: null,
    },
    imageAlt: {
      type: String,
      default: '',
    },
    note: {
      type: String,
      default: '',
    },
    // 飞书平台页 Hero：备注行在 CTA 按钮上方
    noteBeforeCta: {
      type: Boolean,
      default: false,
    },
    hideVisual: {
      type: Boolean,
      default: false,
    },
    showPlayStore: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      playStoreUrl: PLAY_STORE_URL,
      // 文档卖点；图标做成紫色线性风格贴合效果图
      defaultPoints: [
        {
          text: '1 Device Protected',
          icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none"><path d="M12 3l7 3v5c0 4.5-2.8 8.4-7 10-4.2-1.6-7-5.5-7-10V6l7-3z" stroke="currentColor" stroke-width="1.8"/><path d="M9.5 12.2l1.8 1.8 3.4-3.6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        },
        {
          text: 'No Logs & No Ads',
          icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none"><rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" stroke-width="1.8"/><path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
        },
        {
          text: 'Unlimited & Free Forever',
          icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none"><path d="M18.2 8.8a4.2 4.2 0 0 0-6.7-.5L12 8.8l-.5-.5a4.2 4.2 0 1 0 0 6l.5-.5.5.5a4.2 4.2 0 1 0 5.7-5.5z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
        },
        {
          text: 'Global VPN Servers',
          icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none"><circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.8"/><path d="M4.5 12h15M12 4c2.2 2.2 3.3 4.8 3.3 8S14.2 17.8 12 20c-2.2-2.2-3.3-4.8-3.3-8S9.8 6.2 12 4z" stroke="currentColor" stroke-width="1.8"/></svg>',
        },
      ],
      // 图1「Available on」：统一 24 视口 + 实心图标，CSS 控制显示尺寸
      platforms: [
        {
          label: 'Windows',
          to: '/for-windows/',
          icon: '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M3 5.48 10.99 4.4v7.07H3V5.48zm8.99-1.23L21 3v8.55h-9.01V4.25zM3 12.95h7.99v7.07L3 18.94v-5.99zm8.99 0H21V21l-9.01-1.23v-6.82z"/></svg>',
        },
        {
          label: 'macOS',
          to: '/for-mac/',
          icon: '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>',
        },
        {
          label: 'Android',
          to: 'https://www.vpn-free.com/download/',
          external: true,
          icon: '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M17.6 9.48l1.69-2.92a.5.5 0 0 0-.86-.5l-1.72 2.97A10.3 10.3 0 0 0 12 8c-1.9 0-3.66.6-5.11 1.63L5.17 6.06a.5.5 0 1 0-.86.5l1.69 2.92A9.96 9.96 0 0 0 2 17.5V18a1.5 1.5 0 0 0 1.5 1.5H5V15a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v4.5h6V15a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v4.5h1.5A1.5 1.5 0 0 0 20 18v-.5a9.96 9.96 0 0 0-2.4-8.02zM8.5 13.25a1.1 1.1 0 1 1 0-2.2 1.1 1.1 0 0 1 0 2.2zm7 0a1.1 1.1 0 1 1 0-2.2 1.1 1.1 0 0 1 0 2.2z"/></svg>',
        },
        {
          label: 'iPhone/iPad',
          to: '/for-iphone/',
          icon: '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M15.5 2h-7A2.5 2.5 0 0 0 6 4.5v15A2.5 2.5 0 0 0 8.5 22h7a2.5 2.5 0 0 0 2.5-2.5v-15A2.5 2.5 0 0 0 15.5 2zm-3.5 18.2a1.1 1.1 0 1 1 0-2.2 1.1 1.1 0 0 1 0 2.2zM16 16.5H8V5h8v11.5z"/><path fill="#fff" d="M9.15 9.55h1.05c.55 0 .95.14 1.2.42.24.27.36.64.36 1.1 0 .48-.12.86-.37 1.14-.25.28-.66.42-1.22.42h-.52v1.62H8.55V9.55h.6zm.6 2.28h.42c.24 0 .42-.06.54-.17.12-.12.18-.3.18-.54 0-.23-.06-.4-.17-.52-.11-.11-.28-.17-.52-.17h-.45v1.4zm3.2-2.28h1.85c.52 0 .93.13 1.22.4.3.26.45.64.45 1.13 0 .5-.15.89-.46 1.16-.3.27-.72.4-1.25.4h-.8v2.16h-1.01V9.55zm1.01 2.32h.72c.24 0 .42-.05.54-.16.13-.11.19-.28.19-.5s-.06-.4-.18-.51c-.12-.11-.3-.17-.55-.17h-.72v1.34z"/></svg>',
        },
        {
          label: 'Linux',
          to: '/for-linux/',
          icon: '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M12.5 2.2c-1.55 0-2.7 1.55-2.7 3.55 0 .55.1 1.05.3 1.5-.95.4-1.7 1.35-2.05 2.55-.55 1.7-.1 3.55.75 4.7-.55.75-.85 1.7-.85 2.7 0 1.7 1.05 3.05 2.45 3.25-.2.55-.3 1.15-.3 1.7 0 .95.55 1.7 1.4 1.7.65 0 1.15-.4 1.35-1.05h1.2c.2.65.7 1.05 1.35 1.05.85 0 1.4-.75 1.4-1.7 0-.55-.1-1.15-.3-1.7 1.4-.2 2.45-1.55 2.45-3.25 0-1-.3-1.95-.85-2.7.85-1.15 1.3-3 .75-4.7-.35-1.2-1.1-2.15-2.05-2.55.2-.45.3-.95.3-1.5 0-2-1.15-3.55-2.7-3.55zm-1.85 5.55c.4 0 .7.35.7.8s-.3.8-.7.8-.7-.35-.7-.8.3-.8.7-.8zm3.45 0c.4 0 .7.35.7.8s-.3.8-.7.8-.7-.35-.7-.8.3-.8.7-.8zM10 12.1c.45.55 1.2.95 2.4.95s1.95-.4 2.4-.95c.2.25.3.55.3.9 0 .95-.95 1.5-2.7 1.5s-2.7-.55-2.7-1.5c0-.35.1-.65.3-.9z"/></svg>',
        },
        {
          label: 'Chrome',
          to: '/for-chrome/',
          icon: '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 3.5a6.48 6.48 0 0 1 5.55 3.2H12A3.3 3.3 0 0 0 8.75 10.3L6.9 7.15A6.47 6.47 0 0 1 12 5.5zm-3.3 6.5A3.3 3.3 0 1 1 12 15.3 3.3 3.3 0 0 1 8.7 12zm.95 4.05L7.8 19.2A6.48 6.48 0 0 1 5.5 12c0-.9.18-1.75.5-2.53l3.1 5.35a3.28 3.28 0 0 0 2.55 1.73zm7.15-1.2A6.47 6.47 0 0 1 12 18.5a6.4 6.4 0 0 1-2.45-.48l1.85-3.2a3.28 3.28 0 0 0 3.2.03l2.2 1.5z"/></svg>',
        },
        {
          label: 'Firefox',
          to: '/for-firefox/',
          icon: '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M20.48 9.18c.1-.6.12-1.18.07-1.7a8.4 8.4 0 0 0-.5-2.05 7.7 7.7 0 0 0-1.55-2.35C16.7 1.45 14.55.7 12.2.7c-1.55 0-3.05.4-4.35 1.15C6.2.75 4.55.9 3.2 1.7c.55.95 1.35 1.7 2.3 2.2-.95.35-1.8.95-2.4 1.75-.45.6-.75 1.3-.85 2.05.7-.15 1.4-.15 2.1 0A7.8 7.8 0 0 0 2.3 11c0 5.3 4.3 9.6 9.6 9.6 4.55 0 8.35-3.15 9.35-7.4.35-1.35.35-2.8.23-4.02zM12 18.7a6.1 6.1 0 0 1-5.55-3.55c.85.55 1.85.9 2.95.9 2.85 0 5.15-2.2 5.15-5.05 0-.45-.05-.9-.2-1.3.85.7 1.4 1.75 1.4 2.95A6.1 6.1 0 0 1 12 18.7z"/></svg>',
        },
        {
          label: 'Chromebook',
          icon: '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M20.5 5h-17A1.5 1.5 0 0 0 2 6.5v9A1.5 1.5 0 0 0 3.5 17H11v2H7.5a.75.75 0 0 0 0 1.5h9a.75.75 0 0 0 0-1.5H13v-2h7.5A1.5 1.5 0 0 0 22 15.5v-9A1.5 1.5 0 0 0 20.5 5zM12 14.25A3.25 3.25 0 1 1 12 7.75a3.25 3.25 0 0 1 0 6.5zm0-5a1.75 1.75 0 1 0 0 3.5 1.75 1.75 0 0 0 0-3.5z"/></svg>',
        },
        {
          label: 'Android TV',
          icon: '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M20.5 4.5h-17A1.5 1.5 0 0 0 2 6v10.5A1.5 1.5 0 0 0 3.5 18H10v1.5H7.75a.75.75 0 0 0 0 1.5h8.5a.75.75 0 0 0 0-1.5H14V18h6.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5zM15.9 9.35l.75-1.3a.35.35 0 1 0-.6-.35l-.75 1.3A4.3 4.3 0 0 0 12 8.2c-.95 0-1.85.3-2.6.8l-.75-1.3a.35.35 0 1 0-.6.35l.75 1.3A4.35 4.35 0 0 0 7.65 12.5v.35c0 .45.35.8.8.8h.55V12.1c0-.2.15-.35.35-.35h.45c.2 0 .35.15.35.35v1.55h3.7V12.1c0-.2.15-.35.35-.35h.45c.2 0 .35.15.35.35v1.55h.55c.45 0 .8-.35.8-.8v-.35c0-1.35-.7-2.55-1.75-3.15zM10.2 11.4a.55.55 0 1 1 0-1.1.55.55 0 0 1 0 1.1zm3.6 0a.55.55 0 1 1 0-1.1.55.55 0 0 1 0 1.1z"/></svg>',
        },
        {
          label: 'Fire TV',
          icon: '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M20.5 4.5h-17A1.5 1.5 0 0 0 2 6v10.5A1.5 1.5 0 0 0 3.5 18H10v1.5H7.75a.75.75 0 0 0 0 1.5h8.5a.75.75 0 0 0 0-1.5H14V18h6.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5zm-3.35 8.85a.75.75 0 0 1-1.05.1c-1.05-.9-2.35-1.4-3.85-1.4s-2.8.5-3.85 1.4a.75.75 0 1 1-.95-1.15c1.3-1.1 2.95-1.75 4.8-1.75s3.5.65 4.8 1.75a.75.75 0 0 1 .1 1.05zm1.75-2.55a.75.75 0 0 1-1.05.15c-1.45-1.2-3.35-1.9-5.4-1.9s-3.95.7-5.4 1.9a.75.75 0 1 1-.9-1.2c1.7-1.4 3.9-2.2 6.3-2.2s4.6.8 6.3 2.2a.75.75 0 0 1 .15 1.05z"/></svg>',
        },
      ],
    };
  },
  computed: {
    heroPoints() {
      // 显式传入空数组表示文档无卖点列表（如 Linux 页），不要回退到首页默认卖点
      if (Array.isArray(this.points)) {
        if (!this.points.length) {
          return [];
        }
        return this.points.map((item) => {
          if (typeof item === 'string') {
            return {
              text: item,
              icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none"><path d="M12 3l7 3v5c0 4.5-2.8 8.4-7 10-4.2-1.6-7-5.5-7-10V6l7-3z" stroke="currentColor" stroke-width="1.8"/><path d="M9.5 12.2l1.8 1.8 3.4-3.6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
            };
          }
          return item;
        });
      }
      return this.defaultPoints;
    },
    normalizedDescriptionParts() {
      if (!Array.isArray(this.descriptionParts) || !this.descriptionParts.length) {
        return [];
      }
      return this.descriptionParts.map((part) => {
        if (typeof part === 'string') {
          return { type: 'text', value: part };
        }
        if (part && (part.type === 'text' || part.type === 'strong') && part.value != null) {
          return part;
        }
        return { type: 'text', value: String(part ?? '') };
      });
    },
  },
  methods: {
    goCta() {
      const target = this.ctaTo || '/download/'
      if (this.$route.path === target) {
        return
      }
      this.$router.push(target).catch(() => {
        window.location.assign(target)
      })
    },
  },
};
</script>

<style lang="scss" scoped>
.hero {
  position: relative;
  overflow: hidden;
  padding: 48px 0 0;
}

.hero__bg {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 85% 20%, rgba(138, 63, 252, 0.16), transparent 60%),
    radial-gradient(ellipse 50% 45% at 10% 80%, rgba(62, 84, 232, 0.1), transparent 55%),
    linear-gradient(180deg, #ffffff 0%, var(--vpn-page, #f6f4ff) 100%);
  pointer-events: none;
}

.hero__inner {
  position: relative;
  width: var(--vpn-content-width);
  max-width: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
  gap: 32px;
  align-items: center;
  box-sizing: border-box;
}

.hero__inner--no-visual {
  grid-template-columns: minmax(0, 1fr);
}

.hero__inner--no-visual .hero__copy {
  max-width: 100%;
}

.hero__copy {
  min-width: 0;
  max-width: 100%;
}

.hero__title {
  margin: 0 0 16px;
  font-size: clamp(32px, 4vw, 48px);
  line-height: 1.15;
  letter-spacing: -0.03em;
  color: var(--vpn-ink, #1f2430);
  font-weight: 800;
}

.hero__desc {
  margin: 0 0 28px;
  max-width: 540px;
  color: var(--vpn-ink-muted, #5b6475);
  font-size: 16px;

  strong {
    color: var(--vpn-ink, #1f2430);
    font-weight: 700;
  }
}

.hero__points {
  list-style: none;
  margin: 0 0 32px;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 20px;
}

.hero__points li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  color: var(--vpn-ink, #1f2430);
}

.hero__point-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(109, 74, 255, 0.1);
  color: var(--vpn-brand, #6d4aff);
  flex-shrink: 0;
}

.hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.hero__note {
  margin: 0;
  color: var(--vpn-ink, #1f2430);
  font-size: 13px;
  line-height: 1.6;
  font-weight: 600;
}

.hero__note--before {
  margin-bottom: 16px;

  strong {
    font-weight: 700;
    color: var(--vpn-ink, #1f2430);
  }
}

.hero__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  min-width: 168px;
  padding: 8px 28px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.hero__btn--primary {
  background-color: #6d4aff;
  color: #ffffff;
  box-shadow: 0 10px 24px rgba(109, 74, 255, 0.35);
}

.hero__btn--primary:hover {
  background-color: #5b3fe8;
  color: #ffffff;
  transform: translateY(-1px);
}

.hero__btn--play {
  background-color: #ffffff;
  color: #6d4aff;
  border: 1px solid #e8e4f8;
  box-shadow: 0 6px 18px rgba(31, 36, 48, 0.06);
}

.hero__btn--play:hover {
  border-color: rgba(109, 74, 255, 0.35);
  transform: translateY(-1px);
}

.hero__platforms {
  position: relative;
  z-index: 1;
  // 与上方按钮拉开间距
  padding: 40px 0 48px;
}

.hero__platforms-inner {
  width: var(--vpn-content-width);
  margin: 0 auto;
}

.hero__platforms-title {
  margin: 0 0 18px;
  font-size: clamp(18px, 2vw, 22px);
  color: var(--vpn-ink, #1f2430);
  font-weight: 700;
  text-align: center;
}

.hero__platforms-list {
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(10, minmax(0, 1fr));
  border: 1px solid rgba(180, 170, 220, 0.55);
  border-radius: 16px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 8px 24px rgba(90, 60, 180, 0.06);
}

.hero__platforms-list li {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  min-height: 118px;
  padding: 20px 10px;
  border-right: 1px solid rgba(180, 170, 220, 0.45);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(244, 240, 255, 0.85) 100%);

  &:last-child {
    border-right: 0;
  }
}

.hero__platforms-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  height: 100%;
  color: inherit;
  text-decoration: none;
  transition: color 0.2s ease, transform 0.2s ease;
}

.hero__platforms-link:not(.hero__platforms-link--static):hover {
  color: var(--vpn-brand, #6d4aff);
  transform: translateY(-2px);
}

.hero__platforms-link--static {
  cursor: default;
}

.hero__platforms-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: #1a1f36;
  flex-shrink: 0;

  ::v-deep svg {
    width: 30px !important;
    height: 30px !important;
    display: block;
    overflow: visible;
    shape-rendering: geometricPrecision;
  }

  ::v-deep path {
    vector-effect: non-scaling-stroke;
  }
}

.hero__platforms-label {
  font-size: 12px;
  line-height: 1.25;
  color: #2a3348;
  font-weight: 500;
}

.hero__visual {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  max-width: 100%;
  // 防止 img 固有宽度把整页撑破
  min-width: 0;
  box-sizing: border-box;
}

.hero__platform-image {
  display: block;
  width: 100%;
  max-width: 520px;
  max-height: 430px;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 20px 40px rgba(91, 63, 232, 0.16));
}

.hero__privacy {
  width: 58%;
  max-width: 320px;
  height: auto;
  display: block;
  flex: 1 1 0;
  min-width: 0;
  border-radius: 24px;
  object-fit: contain;
}

.hero__phone {
  width: 38%;
  max-width: 220px;
  height: auto;
  display: block;
  flex: 1 1 0;
  min-width: 0;
  border-radius: 28px;
  object-fit: contain;
  filter: drop-shadow(0 20px 40px rgba(91, 63, 232, 0.22));
}

@media (max-width: 1200px) {
  .hero__platforms-list {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  .hero__platforms-list li {
    border-bottom: 1px solid rgba(180, 170, 220, 0.45);

    &:nth-child(5n) {
      border-right: 0;
    }

    &:nth-last-child(-n + 5) {
      border-bottom: 0;
    }
  }
}

@media (max-width: 900px) {
  .hero {
    padding: 24px 0 0;
    overflow: hidden;
  }

  .hero__inner {
    width: 100%;
    max-width: 100%;
    padding: 0 16px;
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
  }

  .hero__visual {
    order: -1;
    gap: 8px;
    width: 100%;
    max-width: 100%;
  }

  .hero__platform-image {
    max-width: 520px;
    max-height: none;
  }

  .hero__privacy {
    width: 46%;
    max-width: 180px;
  }

  .hero__phone {
    width: 32%;
    max-width: 120px;
  }

  .hero__title {
    font-size: clamp(24px, 6.5vw, 32px);
    word-wrap: break-word;
    overflow-wrap: anywhere;
  }

  .hero__desc {
    max-width: 100%;
    margin-bottom: 20px;
    font-size: 15px;
    overflow-wrap: anywhere;
  }

  .hero__points {
    grid-template-columns: 1fr;
    gap: 10px;
    margin-bottom: 20px;
  }

  .hero__points li {
    font-size: 13.5px;
  }

  .hero__note {
    font-size: 12.5px;
  }

  .hero__point-icon {
    width: 36px;
    height: 36px;
  }

  .hero__actions {
    gap: 10px;
  }

  .hero__btn {
    flex: 1 1 calc(50% - 5px);
    min-width: 0;
    padding: 8px 16px;
  }

  .hero__platforms {
    padding: 24px 0 28px;
  }

  .hero__platforms-inner {
    width: 100%;
    max-width: 100%;
    padding: 0 16px;
    box-sizing: border-box;
  }

  .hero__platforms-list {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .hero__platforms-list li {
    min-height: 96px;
    padding: 14px 6px;
    border-bottom: 1px solid rgba(180, 170, 220, 0.45);
    border-right: 1px solid rgba(180, 170, 220, 0.45);

    &:nth-child(5n) {
      border-right: 1px solid rgba(180, 170, 220, 0.45);
    }

    &:nth-child(3n) {
      border-right: 0;
    }

    &:nth-last-child(-n + 5) {
      border-bottom: 1px solid rgba(180, 170, 220, 0.45);
    }

    &:nth-last-child(-n + 3) {
      border-bottom: 0;
    }

    &:nth-child(10) {
      border-bottom: 0;
    }
  }
}

@media (max-width: 560px) {
  .hero__inner {
    padding: 0 12px;
  }

  .hero__privacy {
    width: 44%;
    max-width: 150px;
  }

  .hero__phone {
    width: 30%;
    max-width: 100px;
  }

  .hero__btn {
    flex: 1 1 100%;
    width: 100%;
  }

  .hero__platforms-inner {
    padding: 0 12px;
  }

  .hero__platforms-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .hero__platforms-list li {
    min-height: 88px;
    padding: 12px 6px;

    &:nth-child(3n) {
      border-right: 1px solid rgba(180, 170, 220, 0.45);
    }

    &:nth-child(2n) {
      border-right: 0;
    }

    &:nth-last-child(-n + 3) {
      border-bottom: 1px solid rgba(180, 170, 220, 0.45);
    }

    &:nth-last-child(-n + 2) {
      border-bottom: 0;
    }
  }

  .hero__platforms-icon {
    width: 34px;
    height: 34px;

    ::v-deep svg {
      width: 26px !important;
      height: 26px !important;
    }
  }

  .hero__platforms-label {
    font-size: 11.5px;
  }
}
</style>
